import React, { FunctionComponent, useEffect, useState } from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import styled from 'styled-components';
import useSWRMutation from 'swr/mutation';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { KeyedMutator } from 'swr';

import {
  AchDepositRequest,
  AchRelationship,
  Constraints,
  Transfer,
  TransferIraContributionType
} from '@tradingblock/types';
import { useApi } from '../../../context';
import { useData } from '../../../state/selectors';
import { DefaultApplicationModel } from '../../../types';
import config from '../../../config';
import {
  Button,
  Loading,
  parseNumber,
  Text,
  useLocalize
} from '@tradingblock/components';
import { ApiResponse } from '@tradingblock/api';
import FundingRequestConfirmationModal from '../components/FundingRequestConfirmationModal';
import FundingRequestReview from '../components/FundingRequestReview';
import { FundYourAccountView, FundYourAccountViews } from '../FundYourAccount';
import FundingRequestStepForm from '../components/FundingRequestStepForm';

const StyledFundingRequestStep = styled.div`
  .loading-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fa-check-circle {
    color: var(--teal);
    font-size: 3em;
  }

  .congratulations {
    margin-bottom: 0;
  }

  .cancel-request {
    margin-bottom: 1rem !important;
  }

  .progress-stepper-navigation {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .btn-link {
    text-transform: unset;
    text-decoration: unset;
    letter-spacing: unset;
    font-weight: unset;
  }

  .notes {
    margin-top: 1.5rem;
  }

  .funding-request-submitted {
    text-align: center;
  }

  .btn-link-solo {
    width: fit-content;
    padding-right: unset;
  }

  .btn-primary {
    width: 100%;
  }
`;

export interface FormValues {
  amount: number | string;
  applyToPriorYear?: boolean;
  transferIraContributionType?: TransferIraContributionType;
};

interface FundingRequestStepProps extends LocalizeContextProps {
  iraConstraints?: Constraints;
  achRelationship: AchRelationship;
  setCurrentFundYourAccountView: React.Dispatch<React.SetStateAction<FundYourAccountView>>;
  workingOrCompletedAchTransfer: Transfer | null;
  setActiveStepIndexOverride: React.Dispatch<React.SetStateAction<number | null>>;
  getTransfersData: KeyedMutator<ApiResponse<Transfer[]> | null>;
};

const FundingRequestStep: FunctionComponent<FundingRequestStepProps> = ({
  translate,
  iraConstraints,
  achRelationship,
  setCurrentFundYourAccountView,
  setActiveStepIndexOverride,
  workingOrCompletedAchTransfer,
  getTransfersData,
}) => {
  const api = useApi();

  const application = useData(d => d.data.application);

  const { accountId } = application || DefaultApplicationModel;

  const { getTranslationId } = useLocalize();

  const isIraAccount = !!iraConstraints;

  const [showErrorSubmittingFundingRequest, setShowErrorSubmittingFundingRequest] = useState<boolean>(false);
  const [showFundingRequestConfirmationModal, setShowFundingRequestConfirmationModal] = useState<boolean>(false);
  const [formValuesForConfirmation, setFormValuesForConfirmation] = useState<FormValues | null>(null);
  const [createdTransfer, setCreatedTransfer] = useState<Transfer | null>(null);
  const [showApplyToPriorYearField, setShowApplyToPriorYearField] = useState<boolean>(false);

  const {
    trigger: triggerCreateAchDeposit,
    error: errorCreatingAchDeposit,
    isMutating: isCreatingAchDeposit,
  } = useSWRMutation(
    `${config.tradingApi}/Cashiering/${accountId}/Transfers/Achs/Deposit`,
    (_url: string, { arg }: { arg: { accountId: number, createAchDepositRequestPayload: AchDepositRequest }}) =>
      api.cashiering.transfers.achDeposit(arg.accountId, arg.createAchDepositRequestPayload)
  );

  const onSubmit = async (values: FormValues) => {
    const amountInputElement = document.getElementById('amount');

    amountInputElement && amountInputElement.blur();

    setShowFundingRequestConfirmationModal(true);
    setFormValuesForConfirmation(values);
  };

  const handleFundingRequestConfirmationClick = async () => {
    setShowFundingRequestConfirmationModal(false);

    if (accountId && formValuesForConfirmation) {
      setShowErrorSubmittingFundingRequest(false);

      const amount = typeof formValuesForConfirmation.amount === 'string' ? parseNumber(formValuesForConfirmation.amount) : formValuesForConfirmation.amount;

      const {
        responseCode: createAchDepositResponseCode,
        payload: transfer,
      } = await triggerCreateAchDeposit(
        {
          accountId,
          createAchDepositRequestPayload: {
            amount,
            relationshipId: achRelationship.id,
            ...isIraAccount && {
              iraContribution: {
                type: formValuesForConfirmation.transferIraContributionType as TransferIraContributionType,
                applyToPriorYear: formValuesForConfirmation.applyToPriorYear !== undefined ? formValuesForConfirmation.applyToPriorYear : false,
              }
            }
          }
        }
      ) as ApiResponse<Transfer>;

      if (createAchDepositResponseCode === 0) {
        setCreatedTransfer(transfer);
        setActiveStepIndexOverride(3);
      }

      if (createAchDepositResponseCode !== 0) {
        setShowErrorSubmittingFundingRequest(true);
      }
    }
  };

  useEffect(() => {
    if (errorCreatingAchDeposit) {
      setShowErrorSubmittingFundingRequest(true);
    }
  }, [errorCreatingAchDeposit]);

  if (createdTransfer || workingOrCompletedAchTransfer) {
    return (
      <StyledFundingRequestStep>
        <div className="d-flex justify-content-center align-items-center flex-direction-col">
          <i className="fal fa-check-circle m-1" />
          <p className="congratulations"><Text id="fundYourAccountComponent.congratulations" /></p>
          <p className="funding-request-submitted"><Text id="fundYourAccountComponent.fundingRequestSubmitted" /></p>
          <p>
            <Text
              id="fundYourAccountComponent.checkStatusOfRequest"
              data={{ requestType: <Text id="fundYourAccountComponent.funding" />}}
            />
          </p>
          <p className="cancel-request"><Text id="fundYourAccountComponent.cancelFundingRequest" /></p>
        </div>

        <FundingRequestReview submittedTransfer={createdTransfer || workingOrCompletedAchTransfer} achRelationship={achRelationship} />

        <div className="progress-stepper-navigation">
          <Button
            className="btn-link btn-link-solo"
            onClick={() => {
              setCurrentFundYourAccountView(FundYourAccountViews.FundingMethods);
              getTransfersData();
            }}
          >
            <Text id="btns.backToFundingMethods" />
          </Button>
        </div>
      </StyledFundingRequestStep>
    );
  }

  return (
    <StyledFundingRequestStep>
      {isCreatingAchDeposit && <div className="loading-container"><Loading /></div>}

      {!isCreatingAchDeposit && (
        <>
          <div className="notes">
            <p><Text id="fundYourAccountComponent.fundingRequestStepNotes" /></p>
            <p><Text id="fundYourAccountComponent.newFundingRequest" /></p>
          </div>

          <Formik
            validateOnChange
            validationSchema={
              Yup
              .object<FormValues>()
              .shape(
                {
                  amount: Yup
                  .mixed()
                  .test(
                    'amountCheck',
                    translate(getTranslationId({ id: 'fundYourAccountComponent.enterValidAmount', type: 'error' })) as string,
                    (amount) => {
                      let amountNum;

                      if (typeof amount === 'string') {
                        if (!amount) {
                          return false;
                        }
                        
                        amountNum = parseNumber(amount);
                      } else {
                        amountNum = amount;
                      }

                      if (isNaN(amountNum) || amountNum <= 0 || amountNum > 50000) {
                        return false;
                      }

                      return true;
                    }
                  ),
                  ...isIraAccount && {
                    transferIraContributionType: Yup.string().required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string),
                    ...showApplyToPriorYearField && { applyToPriorYear: Yup.boolean().required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string) },
                  }
                }
              )
            }
            onSubmit={onSubmit}
            initialValues={{
              amount: 0,
              ...isIraAccount && {
                applyToPriorYear: undefined,
                transferIraContributionType: undefined,
              },
            }}
          >
            <FundingRequestStepForm
              iraConstraints={iraConstraints}
              showErrorSubmittingFundingRequest={showErrorSubmittingFundingRequest}
              setShowApplyToPriorYearField={setShowApplyToPriorYearField}
              showApplyToPriorYearField={showApplyToPriorYearField}
            />
          </Formik>
          
          <div className="progress-stepper-navigation">
            <Button
              className="btn-link btn-link-solo"
              onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingMethodSetupRequestStep)}
            >
              <Text id="btns.back" />
            </Button>
          </div>
        </>
      )}

      {
        showFundingRequestConfirmationModal &&
        formValuesForConfirmation &&
        <FundingRequestConfirmationModal
          formValuesForConfirmation={formValuesForConfirmation}
          achRelationship={achRelationship}
          onSubmit={handleFundingRequestConfirmationClick}
          show={showFundingRequestConfirmationModal}
          closeModal={() => setShowFundingRequestConfirmationModal(false)}
        />
      }
    </StyledFundingRequestStep>
  );
};

export default withLocalize(FundingRequestStep);